import { createContext, useContext, useMemo, useReducer, ReactNode } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"
import { ModelMetadata } from "resources/models"

export interface FactorSensitivityModelMetadata extends Omit<ModelMetadata, "categories" | "factors"> {
  categories?: ModelMetadata["categories"]
  factors?: ModelMetadata["factors"]
}

export type SensitivityType = "predicted" | "historical"

const PREFERENCE_SUBTYPES: Record<SensitivityType, string> = {
  predicted: "graph-factor-sensitivity-predicted",
  historical: "graph-factor-sensitivity-historical",
}
const PREFERENCE_FIELDS = ["selectedCategory"]

interface FactorSensitivityState {
  selectedCategory: string
}

const DEFAULT_STATE: FactorSensitivityState = {
  selectedCategory: "total",
}

type FactorSensitivityAction =
  | { type: "setSelectedCategory", selectedCategory: string }
  | { type: "setPreferences", preferences: Partial<FactorSensitivityState> | null }

function reducer(current: FactorSensitivityState, action: FactorSensitivityAction): FactorSensitivityState {
  return produce(current, draft => {
    switch (action.type) {
      case "setSelectedCategory":
        draft.selectedCategory = action.selectedCategory
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setPreferences(preferences: Partial<FactorSensitivityState>): FactorSensitivityAction {
  return { type: "setPreferences", preferences }
}

export interface FactorSensitivityContextValue extends FactorSensitivityState {
  sensitivityType: SensitivityType
  isPreferencesLoaded: boolean
  setSelectedCategory: (selectedCategory: string) => void
}

export const FACTOR_SENSITIVITY_CONTEXT = createContext<FactorSensitivityContextValue | undefined>(undefined)

export function useFactorSensitivityContext(): FactorSensitivityContextValue {
  const ctx = useContext(FACTOR_SENSITIVITY_CONTEXT)
  if (ctx === undefined) {
    throw new Error("useFactorSensitivityContext must be used within a FactorSensitivityContext provider")
  }
  return ctx
}

interface FactorSensitivityContextProps {
  sensitivityType: SensitivityType
  children: ReactNode
}

export function FactorSensitivityContext({ sensitivityType, children }: FactorSensitivityContextProps) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)
  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPES[sensitivityType],
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    setSelectedCategory: (selectedCategory: string) => dispatch({ type: "setSelectedCategory", selectedCategory }),
  }), [dispatch])

  const contextValue = useMemo((): FactorSensitivityContextValue => ({
    ...state,
    sensitivityType,
    ...contextUpdaters,
    isPreferencesLoaded,
  }), [state, sensitivityType, contextUpdaters, isPreferencesLoaded])

  return <FACTOR_SENSITIVITY_CONTEXT.Provider value={contextValue}>
    {children}
  </FACTOR_SENSITIVITY_CONTEXT.Provider>
}

interface BoundContextProps {
  children: ReactNode
}

export function HistoricalFactorSensitivityContext({ children }: BoundContextProps) {
  return <FactorSensitivityContext sensitivityType="historical">{children}</FactorSensitivityContext>
}

export function PredictedFactorSensitivityContext({ children }: BoundContextProps) {
  return <FactorSensitivityContext sensitivityType="predicted">{children}</FactorSensitivityContext>
}
